import axios, { type AxiosInstance, type AxiosResponse, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse, ApiError } from './types'
import type { TokenStorage } from './ports/token-storage'
import { CORRELATION_ID_HEADER, newCorrelationId } from './correlation-id'

/**
 * Configuración para crear el cliente API
 */
export interface ApiClientConfig {
  /**
   * URL base de la API (sin versión)
   */
  baseUrl: string

  /**
   * Versión de la API (ej: 'v1')
   */
  apiVersion?: string

  /**
   * Almacenamiento de tokens (inyección de dependencia)
   */
  tokenStorage: TokenStorage

  /**
   * Timeout en milisegundos (default: 10000)
   */
  timeout?: number

  /**
   * Callback cuando el token expira (401)
   */
  onUnauthorized?: () => void
}

/**
 * Estado interno del cliente
 */
let clientInstance: AxiosInstance | null = null
let clientConfig: ApiClientConfig | null = null

/**
 * Estado del refresco de token. Evita disparar múltiples /auth/refresh
 * cuando varias peticiones reciben 401 a la vez: el primero refresca y
 * el resto espera en la cola al nuevo token.
 */
let isRefreshing = false
let refreshQueue: Array<(token: string | null) => void> = []

const flushQueue = (token: string | null): void => {
  refreshQueue.forEach((resume) => resume(token))
  refreshQueue = []
}

/**
 * Identificador de correlación que llevaba la petición que falló.
 *
 * Las cabeceras de una petición son `AxiosHeaders`, pero tras un reintento pueden llegar como
 * objeto plano: se leen de las dos formas antes de darse por vencido.
 */
const sentCorrelationId = (error: AxiosError<ApiError>): string | undefined => {
  const headers = error.config?.headers as
    | { get?: (name: string) => unknown; [key: string]: unknown }
    | undefined
  if (!headers) {
    return undefined
  }
  const value = typeof headers.get === 'function'
    ? headers.get(CORRELATION_ID_HEADER)
    : headers[CORRELATION_ID_HEADER]
  return typeof value === 'string' ? value : undefined
}

/**
 * Da formato consistente a un error de Axios.
 */
const toApiError = (error: AxiosError<ApiError>): ApiError => {
  const responseData = error.response?.data as Record<string, unknown> | undefined
  const details = responseData?.details as Record<string, unknown> | undefined
  return {
    message: error.response?.data?.message || error.message || 'Error desconocido',
    status: error.response?.status || 0,
    timestamp: error.response?.data?.timestamp || new Date().toISOString(),
    path: error.config?.url || '',
    errorCode: responseData?.errorCode as string | undefined,
    details,
    errorId: details?.errorId as string | undefined,
    // El backend devuelve el identificador de correlación en el cuerpo del error; si la
    // respuesta no llegó a formarse (fallo de red, timeout), vale el que mandó esta petición.
    correlationId: (details?.correlationId as string | undefined) ?? sentCorrelationId(error),
  }
}

/**
 * Indica si la URL corresponde a un endpoint de autenticación público
 * (login/register/refresh), que no debe disparar el flujo de refresco.
 */
const isAuthEndpoint = (url?: string): boolean =>
  !!url && /\/auth\/(login|register|refresh|account-recovery)/.test(url)

/**
 * Normaliza una ruta eliminando barras finales
 */
const normalizePath = (value: string): string => value.replace(/\/+$/, '')

/**
 * Construye la URL base completa
 */
const buildBaseUrl = (baseUrl: string, apiVersion?: string): string => {
  const normalizedUrl = normalizePath(baseUrl)

  if (!apiVersion) {
    return normalizedUrl
  }

  const versionSegment = `/${apiVersion.trim()}`
  if (normalizedUrl.endsWith(versionSegment)) {
    return normalizedUrl
  }

  return `${normalizedUrl}${versionSegment}`
}

/**
 * Crea una instancia del cliente API configurada
 */
export const createApiClient = (config: ApiClientConfig): AxiosInstance => {
  clientConfig = config

  const instance = axios.create({
    baseURL: buildBaseUrl(config.baseUrl, config.apiVersion),
    timeout: config.timeout ?? 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Interceptor para agregar token de autenticación e identificador de correlación
  instance.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const token = config.tokenStorage.getToken()
      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`
      }
      // Una petición reintentada tras refrescar el token conserva el identificador del
      // intento original: es la misma acción del usuario y debe leerse como una sola.
      if (!requestConfig.headers.has(CORRELATION_ID_HEADER)) {
        requestConfig.headers.set(CORRELATION_ID_HEADER, newCorrelationId())
      }
      return requestConfig
    },
    (error) => Promise.reject(error)
  )

  // Interceptor para manejar respuestas y errores
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse<unknown>>) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined

      const isUnauthorized = error.response?.status === 401

      // Ante un 401 en una petición autenticada intentamos renovar el access
      // token con el refresh token antes de cerrar sesión. Los endpoints de
      // auth (login/register/refresh) y los reintentos quedan excluidos para
      // no entrar en bucle.
      if (
        isUnauthorized &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest.url)
      ) {
        const refreshToken = config.tokenStorage.getRefreshToken()

        if (!refreshToken) {
          config.onUnauthorized?.()
          return Promise.reject(toApiError(error))
        }

        // Si ya hay un refresco en curso, esperamos su resultado.
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            refreshQueue.push((newToken) => {
              if (newToken) {
                originalRequest._retry = true
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                resolve(instance(originalRequest))
              } else {
                reject(toApiError(error))
              }
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          // Llamada aislada (axios directo) para no re-entrar en este interceptor.
          const refreshUrl = `${buildBaseUrl(config.baseUrl, config.apiVersion)}/auth/refresh`
          const refreshResponse = await axios.post<ApiResponse<{ token: string; refreshToken: string }>>(
            refreshUrl,
            { refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          )

          const refreshedCredentials = refreshResponse.data?.data
          const newToken = refreshedCredentials?.token
          const newRefreshToken = refreshedCredentials?.refreshToken
          if (!newToken || !newRefreshToken) {
            throw new Error('Respuesta de refresh sin tokens rotados')
          }

          config.tokenStorage.setToken(newToken)
          config.tokenStorage.setRefreshToken(newRefreshToken)
          isRefreshing = false
          flushQueue(newToken)

          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return instance(originalRequest)
        } catch {
          // El refresh token también caducó/es inválido: cerramos sesión.
          isRefreshing = false
          flushQueue(null)
          config.onUnauthorized?.()
          return Promise.reject(toApiError(error))
        }
      }

      return Promise.reject(toApiError(error))
    }
  )

  clientInstance = instance
  return instance
}

/**
 * Obtiene la instancia del cliente API
 * @throws Error si el cliente no ha sido inicializado
 */
export const getApiClient = (): AxiosInstance => {
  if (!clientInstance) {
    throw new Error(
      'API client no inicializado. Llama a createApiClient() primero.'
    )
  }
  return clientInstance
}

/**
 * Obtiene la configuración actual del cliente
 */
export const getApiClientConfig = (): ApiClientConfig | null => clientConfig

/**
 * Verifica si el cliente está inicializado
 */
export const isApiClientInitialized = (): boolean => clientInstance !== null

/**
 * Resetea el cliente (útil para testing)
 */
export const resetApiClient = (): void => {
  clientInstance = null
  clientConfig = null
}

// ============== HELPERS ==============

/**
 * Extrae data de una respuesta API exitosa
 */
export const handleApiResponse = <T>(response: AxiosResponse<ApiResponse<T>>): T => {
  if (response.data.success) {
    return response.data.data
  }
  throw new Error(response.data.message)
}

/**
 * Maneja y re-lanza errores de API
 */
export const handleApiError = (error: ApiError): never => {
  console.error('API Error:', error)
  throw error
}
